/**
 * Кто вошёл — один источник на всё приложение.
 *
 * Ответ сервера (`/auth/me`) — единственная правда о правах. Клиент по нему
 * решает, что ПОКАЗЫВАТЬ, и никогда — что РАЗРЕШЕНО: разрешение проверяет
 * доменный сервис на сервере. Спрятанная кнопка ничего не защищает, поэтому
 * защитой она здесь и не считается; она только избавляет человека от заведомо
 * бессмысленного действия.
 *
 * Права разрешаются на дату контекста анализа: привязка аккаунта к хозяйству
 * имеет период, и на прошлый срез нельзя смотреть сегодняшними правами.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAnalysisContext } from "../analysis/context";
import { api, ApiError, type PrincipalOut } from "../api/client";
import { notePrincipal } from "../api/session";
import { ROUTES } from "../lib/routes";

export interface AuthValue {
  principal: PrincipalOut | null;
  /** Первая загрузка ещё идёт: «не вошёл» пока утверждать нельзя. */
  loading: boolean;
  error: ApiError | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Пауза перед перечитыванием `/auth/me` при смене среза (вход уже известен). */
export const ME_SETTLE_MS = 300;

/** Кто это для памяти ответов: имя вошедшего или аноним. */
function identityOf(value: PrincipalOut): string | null {
  return value.is_authenticated ? (value.username ?? null) : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const context = useAnalysisContext();
  const [principal, setPrincipal] = useState<PrincipalOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [generation, setGeneration] = useState(0);

  // Перечитывание при смене среза (`as_of`) — не «вход ещё не известен»:
  // поднимать `loading` здесь значило бы снять всю оболочку (`RequirePersona`)
  // и сбросить состояние экрана на каждом шаге даты (agroai-6al.1, ползунок
  // карты). Ожидание показывается только до первого ответа.
  const known = useRef(false);
  known.current = principal !== null;

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .me(context.params)
        .then((value) => {
          if (alive) {
            notePrincipal(identityOf(value));
            setPrincipal(value);
            setError(null);
          }
        })
        .catch((cause) => {
          if (alive) setError(cause as ApiError);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    if (!known.current) {
      setLoading(true);
      void load();
      return () => {
        alive = false;
      };
    }
    // Вход уже известен, сменился срез: перечитать права на новую дату — но
    // не на каждый промежуточный шаг. Поле даты коммитит неполный год
    // («2» → 0002-…, «20» → 0020-…), стрелка на сегменте — шаг за шагом, и
    // на стенде это давало ~20 запросов `/auth/me` в секунду (agroai-6al.39).
    const timer = setTimeout(() => void load(), ME_SETTLE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [context.key, generation]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      // Токен CSRF берётся перед входом: без cookie у формы входа его ещё нет,
      // и первый же POST был бы отклонён.
      await api.csrf();
      const value = await api.login(username, password, context.params);
      // Сброс памяти ответов — ДО того, как экраны нового аккаунта начнут
      // читать: иначе они получили бы ответы, срезанные областью прежнего.
      notePrincipal(identityOf(value));
      setPrincipal(value);
      setError(null);
    },
    [context.params],
  );

  const signOut = useCallback(async () => {
    await api.csrf();
    const value = await api.logout(context.params);
    notePrincipal(identityOf(value));
    setPrincipal(value);
  }, [context.params]);

  const value = useMemo<AuthValue>(
    () => ({
      principal,
      loading,
      error,
      signIn,
      signOut,
      reload: () => setGeneration((count) => count + 1),
    }),
    [principal, loading, error, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * То же, что `useAuth`, но без требования провайдера: оболочечным частям
 * (панель ИИ) вход нужен как подсказка, а не как условие отрисовки, и их
 * тесты монтируют оболочку без `AuthProvider`.
 */
export function useOptionalAuth(): AuthValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth вызван вне AuthProvider");
  return value;
}

/**
 * Хозяйство, от лица которого смотрит экран.
 *
 * Аккаунт хозяйства — своё первое (обычно единственное). Аккаунт с районной
 * областью своего хозяйства не имеет: он обязан выбрать его явно, и `null`
 * здесь означает именно «не выбрано», а не «работ нет».
 */
export function ownActorId(principal: PrincipalOut | null): number | null {
  const first = principal?.actors?.[0];
  return first ? first.id : null;
}

/**
 * Роль решает, где человек НАЧИНАЕТ и какие пункты меню ему показывать, —
 * и никогда, что ему разрешено.
 *
 * Различение сделано по тем полям, которые сервер уже отдаёт (`district_scope`,
 * `actors`), а не по строкам `roles`: список ролей — словарь домена, он
 * меняется без ведома клиента, и завязывать на него разметку значит завести
 * второе определение роли рядом с серверным.
 */
export type RoleKind =
  /** Не вошёл. Чтение открыто, поэтому меню у него полное. */
  | "anonymous"
  /** Область — весь район: хоким, замхокима, диспетчер. */
  | "district"
  /**
   * Аккаунт хозяйства: своё хозяйство есть, района нет.
   *
   * Это область, а НЕ полномочие: проверяющий с областью «свои хозяйства»
   * попадает сюда же. На вопрос «может ли выносить вердикт» отвечает
   * `canVerify`, а не эта роль.
   */
  | "farm"
  /** Вошёл, но ни района, ни хозяйства: область ещё не выдана. */
  | "unscoped";

export function roleKind(principal: PrincipalOut | null): RoleKind {
  if (!principal || !principal.is_authenticated) return "anonymous";
  if (principal.district_scope) return "district";
  return principal.actors.length > 0 ? "farm" : "unscoped";
}

/**
 * Может ли аккаунт выносить вердикт по отчёту.
 *
 * Ответ сервера (`can_verify`), а не вывод из области: проверяющий бывает и с
 * областью «свои хозяйства» — он проверяет только их, но очередь проверки
 * остаётся его рабочим местом. Выводить это из `roleKind` значит завести
 * второе определение полномочия рядом с серверным — и спрятать экран у того,
 * кому он и предназначен.
 *
 * Как и всё остальное здесь, это подсказка интерфейсу: разрешает запись
 * доменный сервис на сервере.
 */
export function canVerify(principal: PrincipalOut | null): boolean {
  return principal?.can_verify === true;
}

/**
 * Может ли аккаунт распоряжаться кампаниями (запуск, объявление, пауза).
 *
 * Единственное, чем аккаунт хокимията отличается от диспетчера-проверяющего:
 * область у обоих районная, право проверять — у обоих. Различать их по строкам
 * `roles` значило бы завести второе определение роли рядом с серверным,
 * поэтому спрашивается ровно то поле, которым сервер сам их и разводит.
 *
 * Как и `canVerify`, это подсказка интерфейсу: разрешает запись сервер.
 */
export function canManageCampaigns(principal: PrincipalOut | null): boolean {
  return principal?.can_manage_campaigns === true;
}

/**
 * Адрес, с которого роль начинает работу.
 *
 * Аккаунт хозяйства открывает не «картину района», а кабинет: `/my/today`
 * отвечает на вопрос «что от меня ждут сейчас», и это его обязанность, тогда
 * как район он смотрит по своему выбору. Остальные начинают с района — это
 * уровень, с которого расходится вся цепочка («район → массив → хозяйство →
 * контур → работа»).
 *
 * Кабинет — три адреса, а не один с параметром: «что ждут», «мои поля» и «мои
 * отчёты» — три разных вопроса, и вкладками они были бы неадресуемы. Дом роли
 * — первый из них, потому что срочен только он.
 *
 * Проверяющий с областью «свои хозяйства» по области — тот же `farm`, но
 * ждут от него не отчёта, а вердикта: уводить его в кабинет значит уводить с
 * рабочего места.
 */
export function homePath(principal: PrincipalOut | null): string {
  return roleKind(principal) === "farm" && !canVerify(principal)
    ? ROUTES.myToday
    : ROUTES.district;
}
